import { useState, Suspense } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { LogIn, FileText, Target, UserPlus, Users } from 'lucide-react'
import SignInForm from '@/components/auth/SignInForm'
import RegisterForm from '@/components/auth/RegisterForm'

type LoginMode = 'signin' | 'register'

export default function LoginPage() {
  const [mode, setMode] = useState<LoginMode>('signin')

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        {/* Header */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0.5, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <Target className="w-8 h-8 text-white" />
          </motion.div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Recruiter</h1>
          <p className="text-gray-600">Open-Book Interview Platform</p>
          <p className="text-sm text-blue-600 font-medium">"Hire for skills, not memory"</p>
        </div>

        {/* Mode Selection Tabs */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-xl mb-4 p-2"
        >
          <div className="flex rounded-lg bg-gray-100 p-1">
            <button
              onClick={() => setMode('signin')}
              className={`flex-1 flex items-center justify-center py-2 px-3 rounded-md text-sm font-medium transition-all ${
                mode === 'signin'
                  ? 'bg-white text-blue-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <LogIn className="w-4 h-4 mr-2" />
              Sign In
            </button>
            <button
              onClick={() => setMode('register')}
              className={`flex-1 flex items-center justify-center py-2 px-3 rounded-md text-sm font-medium transition-all ${
                mode === 'register'
                  ? 'bg-white text-green-600 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Register
            </button>
          </div>
        </motion.div>

        {/* Main Content */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-xl p-8"
        >
          <AnimatePresence mode="wait">
            {mode === 'signin' && (
              <Suspense fallback={
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                </div>
              }>
                <SignInForm
                  key="signin"
                  onSwitch={() => setMode('register')}
                />
              </Suspense>
            )}

            {mode === 'register' && (
              <RegisterForm
                key="register"
                onSwitch={() => setMode('signin')}
              />
            )}
          </AnimatePresence>
        </motion.div>

        {/* Features Preview */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-8 text-center"
        >
          <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mb-2">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <span>Interview Setup</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-2">
                <FileText className="w-5 h-5 text-green-600" />
              </div>
              <span>AI Assessment</span>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mb-2">
                <Target className="w-5 h-5 text-purple-600" />
              </div>
              <span>Real-time Analytics</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}
